import { useState, useEffect, useRef } from "react";
import {
  FiSearch,
  FiPhone,
  FiMail,
  FiMoreVertical,
  FiMessageCircle,
  FiMapPin,
  FiPlus,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { BsBuilding } from "react-icons/bs";
import { BiEditAlt } from "react-icons/bi";
import { HiMail } from "react-icons/hi";
import { contactsApi } from "@/api/api";
import { IContact } from "@/interfaces/Icontact";
import ContactModal from "./ContactModal";
import Avatar from "./avatar";
import SocialLinks from "./socialLinks";
import toast from "react-hot-toast";
import { usePermissions } from "@/contexts/PermissionContext";
import { checkPermissions } from "@/utils/checkPermissions";
import { activitiesApiHooks } from "@/api/activitiesApi";

interface IRecentChat {
  id: string;
  name: string;
  message: string;
  time: string;
  hasEmail?: boolean;
  isRead?: boolean;
}

const Contact = () => {
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [activeTab, setActiveTab] = useState("about");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { permissionLevel } = usePermissions();

  // Fetch activities for selected contact
  const { data: activities = [] } = activitiesApiHooks.useGetActivities(
    [],
    selectedContact?.id?.toString()
  );

  const fetchContacts = async () => {
    try {
      console.log("fetchContacts called - fetching fresh contact data");
      const response = await contactsApi.getContacts();
      const contactsData = response.data;
      console.log(`Fetched ${contactsData.length} contacts`);
      console.log(
        "Contact IDs in response:",
        contactsData.map((c: any) => c.id)
      );
      console.log("Looking for contact with id:", selectedContact?.id);

      // Fetch all contact pictures in parallel
      const contactsWithPictures = await Promise.all(
        contactsData.map(async (contact: IContact) => {
          if (contact.picture) {
            try {
              const pictureResponse = await contactsApi.getContactPicture(
                contact.picture
              );
              const imageUrl = URL.createObjectURL(pictureResponse.data);
              return {
                ...contact,
                pictureBlob: imageUrl,
              };
            } catch (error) {
              console.error(
                `Error fetching picture for contact ${contact.id}:`,
                error
              );
              return contact;
            }
          }
          return contact;
        })
      );

      setContacts(contactsWithPictures);

      // Only set selectedContact if none is currently selected
      if (contactsWithPictures.length > 0 && !selectedContact) {
        console.log("Setting initial selectedContact");
        setSelectedContact(contactsWithPictures[0]);
      } else if (selectedContact) {
        // If we have a selectedContact, try to find it in the updated list
        console.log("Searching for contact in fresh data...");
        console.log(
          "selectedContact.id:",
          selectedContact.id,
          "type:",
          typeof selectedContact.id
        );
        console.log(
          "contactsWithPictures IDs:",
          contactsWithPictures.map((c) => ({ id: c.id, type: typeof c.id }))
        );

        const updatedSelectedContact = contactsWithPictures.find((contact) => {
          const match = String(contact.id) === String(selectedContact.id);
          console.log(
            `Comparing contact.id ${
              contact.id
            } (${typeof contact.id}) with selectedContact.id ${
              selectedContact.id
            } (${typeof selectedContact.id}) = ${match}`
          );
          return match;
        });
        if (updatedSelectedContact) {
          // Update selectedContact with fresh data
          console.log("Updating selectedContact with fresh data:", {
            contactId: selectedContact.id,
            oldPhones: selectedContact.phones?.map((p: any) =>
              typeof p === "string" ? p : p.phone
            ),
            newPhones: updatedSelectedContact.phones?.map((p: any) =>
              typeof p === "string" ? p : p.phone
            ),
            isSameObject: selectedContact === updatedSelectedContact,
          });
          setSelectedContact(updatedSelectedContact);
        } else {
          console.log(
            `Selected contact with id ${selectedContact.id} not found in fresh data`
          );
        }
        // If not found, keep the current selection (don't reset to first)
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    fetchContacts();

    // Cleanup function to revoke object URLs
    return () => {
      contacts.forEach((contact) => {
        if (contact.pictureBlob) {
          URL.revokeObjectURL(contact.pictureBlob);
        }
      });
    };
  }, []);

  // Add debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-select first contact if no contact is selected and contacts exist
  useEffect(() => {
    if (!selectedContact && contacts.length > 0) {
      setSelectedContact(contacts[0]);
    }
  }, [contacts, selectedContact]);

  // Transform activities to recent chats format
  const recentChats: IRecentChat[] = activities.map((activity) => {
    const secretaryName = `${activity.secretary.firstName} ${activity.secretary.lastName}`;
    const contactName = activity.contactPhone?.contact
      ? `${activity.contactPhone.contact.firstName || ""} ${
          activity.contactPhone.contact.lastName || ""
        }`.trim()
      : secretaryName;

    // Format duration
    const duration = activity.durationCall;
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    const durationText =
      minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

    // Build message with more activity details
    let messageParts = [`Duration: ${durationText}`];

    // Add business line if available
    if (activity.businessLine?.business?.name) {
      messageParts.push(`Business: ${activity.businessLine.business.name}`);
    } else if (activity.businessLine?.line?.phone) {
      messageParts.push(`Line: ${activity.businessLine.line.phone}`);
    }

    // Add form data company if available
    if (activity.formData?.company) {
      messageParts.push(`Company: ${activity.formData.company}`);
    } else if (activity.contactPhone?.contact?.company) {
      messageParts.push(`Company: ${activity.contactPhone.contact.company}`);
    }

    const message = messageParts.join(" • ");

    // Format time
    const createdDate = new Date(activity.createdAt);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60)
    );

    let timeText = "";
    if (diffInHours < 1) {
      timeText = "Just now";
    } else if (diffInHours < 24) {
      timeText = `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      timeText = `${diffInDays}d ago`;
    }

    return {
      id: activity.id.toString(),
      name: contactName || secretaryName,
      message: message,
      time: timeText,
      hasEmail: !!activity.secretary.email,
      isRead: activity.isRead,
    };
  });

  // Helper function to check if contact has any social links
  const hasSocialLinks = (contact: IContact | null) => {
    if (!contact?.socialLinks) return false;
    const links = contact.socialLinks;
    return !!(
      links.website ||
      links.facebook ||
      links.twitter ||
      links.linkedin ||
      links.instagram ||
      links.youtube
    );
  };

  const filteredContacts = contacts.filter((contact) => {
    if (!debouncedSearchQuery) return true;

    const searchTerms = debouncedSearchQuery.toLowerCase().split(" ");
    const contactFields = [
      (contact as any).firstName ?? contact.first_name,
      (contact as any).lastName ?? contact.last_name,
      (contact as any).company ?? contact.company_name,
      ...(contact.emails?.map((email) => email.email) || []),
      ...(contact.phones?.map((phone) => phone.phone) || []),
      contact.address?.city,
      contact.address?.state,
      contact.address?.country,
    ].map((field) => (field || "").toLowerCase());

    return searchTerms.every((term) =>
      contactFields.some((field) => field.includes(term))
    );
  });

  const openEditContactModal = (contact: IContact) => {
    console.log(
      "openEditContactModal called with contact:",
      contact?.firstName || contact?.first_name
    );
    setSelectedContact(contact);
    console.log(
      "selectedContact changed to:",
      selectedContact?.firstName || selectedContact?.first_name
    );
    setModalMode("edit");
    setIsModalOpen(true);
  };

  useEffect(() => {
    console.log(
      "selectedContact changed to:",
      selectedContact?.firstName ||
        selectedContact?.first_name ||
        "undefined/null"
    );
    if (selectedContact) {
      console.log("Contact selected, notes:", selectedContact.note);
      console.log(
        "Current phones:",
        selectedContact.phones?.map((p) =>
          typeof p === "string" ? p : p.phone
        )
      );
    } else {
      console.log("No contact selected");
    }
  }, [selectedContact]);

  useEffect(() => {
    if (activeTab === "notes" && selectedContact) {
    }
  }, [activeTab, selectedContact]);

  useEffect(() => {
    if (selectedContact?.note?.content) {
      setNoteInput(selectedContact.note.content);
    } else {
      setNoteInput("");
    }
  }, [selectedContact]);

  const handleSaveNote = async () => {
    if (!selectedContact) return;
    try {
      const updatedContact = {
        ...selectedContact,
        note: {
          content: noteInput,
          updated_at: new Date().toISOString(),
        },
      };
      const response = await contactsApi.editContact(
        String(String(selectedContact.id)!),
        updatedContact
      );
      if (response.status === 200) {
        setSelectedContact(updatedContact);
        setContacts((prev) =>
          prev.map((c) => (c.id === updatedContact.id ? updatedContact : c))
        );
        toast.success("Note updated");
        setIsEditingNote(false);
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to update note");
    }
  };

  const handleDeleteNote = async () => {
    if (!selectedContact) return;
    setShowDeleteNoteModal(true);
  };

  const confirmDeleteNote = async () => {
    if (!selectedContact) return;
    try {
      const updatedContact = {
        ...selectedContact,
        note: null,
      };
      const response = await contactsApi.editContact(
        String(String(selectedContact.id)!),
        updatedContact
      );
      if (response.status === 200) {
        setSelectedContact(updatedContact);
        setContacts((prev) =>
          prev.map((c) => (c.id === updatedContact.id ? updatedContact : c))
        );
        toast.success("Note deleted");
        setIsEditingNote(false);
      }
    } catch (e) {
      toast.error("Failed to delete note");
    }
    setShowDeleteNoteModal(false);
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    try {
      await contactsApi.deleteContact(String(String(selectedContact.id)!));
      const updatedContacts = contacts.filter(
        (c) => c.id !== String(selectedContact.id)
      );
      setContacts(updatedContacts);

      // If there are remaining contacts, select the first one
      if (updatedContacts.length > 0) {
        setSelectedContact(updatedContacts[0]);
      } else {
        setSelectedContact(null);
      }

      toast.success("Contact deleted successfully");
      setShowDeleteContactModal(false);

      // Refetch contacts to ensure data is fresh
      fetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete contact");
    }
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left sidebar with contacts list */}
      <div className="w-64 bg-white border-r border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-extrabold">Contact</h2>
            <button
              onClick={() => {
                setModalMode("add");
                setIsModalOpen(true);
              }}
              className="text-[#78C7D1] hover:text-[#78C7D1] border border-[#78C7D1] rounded-md p-1"
            >
              <FiPlus className="w-3 h-3" />
            </button>
          </div>
          <div className="relative mt-4">
            <input
              type="text"
              placeholder="Search contacts..."
              className="w-full p-2 bg-gray-100 rounded-full pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FiSearch className="absolute top-3 left-3 text-gray-400" />
          </div>
        </div>

        <div className="overflow-y-auto">
          {filteredContacts.map((contact) => (
            <div
              key={contact.id}
              className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                selectedContact?.id === contact.id ? "bg-gray-100" : ""
              }`}
              onClick={() => setSelectedContact(contact)}
            >
              <Avatar
                firstName={
                  (contact as any).firstName ?? contact.first_name ?? ""
                }
                lastName={(contact as any).lastName ?? contact.last_name ?? ""}
                avatarImageUrl={
                  contact.pictureBlob ?? contact.picture ?? undefined
                }
              />
            </div>
          ))}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 bg-white">
        {selectedContact ? (
          <>
            <div className="">
              <div className="px-8 pt-6 pb-4">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center">
                    <div
                      className={`bg-[#78C7D1] text-white rounded-full w-12 h-12 flex items-center justify-center`}
                    >
                      {selectedContact?.pictureBlob ? (
                        <img
                          src={selectedContact.pictureBlob}
                          alt="Contact Avatar"
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <p className="text-white">
                          {`${
                            ((selectedContact as any).firstName ??
                              selectedContact?.first_name)?.[0]
                          }${
                            ((selectedContact as any).lastName ??
                              selectedContact?.last_name)?.[0]
                          }`.toUpperCase()}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {(selectedContact as any).firstName ??
                          selectedContact?.first_name}{" "}
                        {(selectedContact as any).lastName ??
                          selectedContact?.last_name}{" "}
                      </h3>
                      <p className="text-gray-500">
                        {selectedContact?.address?.city ?? ""}
                        {selectedContact?.address?.city ? ", " : ""}
                        {selectedContact?.address?.state ?? ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="">
                      <FiMessageCircle size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        console.log(
                          "Edit button clicked, selectedContact:",
                          selectedContact,
                          "type:",
                          typeof selectedContact,
                          "id:",
                          selectedContact?.id
                        );
                        if (selectedContact && String(selectedContact.id)) {
                          console.log(
                            "selectedContact exists, calling openEditContactModal"
                          );
                          openEditContactModal(selectedContact);
                        } else {
                          console.log(
                            "selectedContact is invalid, showing error"
                          );
                          toast.error("No contact selected");
                        }
                      }}
                      className=""
                    >
                      <BiEditAlt size={16} />
                    </button>
                    <div className="relative" ref={menuRef}>
                      <button
                        className=""
                        onClick={() => setShowMenu((prev) => !prev)}
                      >
                        <FiMoreVertical size={16} />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg z-10">
                          <button
                            className="flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100"
                            onClick={() => {
                              setShowMenu(false);
                              toast("Block feature coming soon");
                            }}
                          >
                            <span className="mr-2">
                              <svg
                                width="18"
                                height="18"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                className="text-gray-400"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  strokeWidth="2"
                                />
                                <line
                                  x1="8"
                                  y1="12"
                                  x2="16"
                                  y2="12"
                                  strokeWidth="2"
                                />
                              </svg>
                            </span>
                            Block
                          </button>
                          {permissionLevel &&
                            checkPermissions(
                              permissionLevel,
                              "delete",
                              "contacts"
                            ) && (
                              <button
                                className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-gray-100"
                                onClick={() => {
                                  setShowDeleteContactModal(true);
                                  setShowMenu(false);
                                }}
                              >
                                <span className="mr-2">
                                  <FiTrash2 className="w-5 h-5 text-red-400" />
                                </span>
                                Delete Contact
                              </button>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-4 border-b border-gray-200">
                  <button
                    className={`py-2 px-4 ${
                      activeTab === "about"
                        ? "text-gray-700 border-b-2 border-green-600"
                        : "text-gray-500"
                    }`}
                    onClick={() => setActiveTab("about")}
                  >
                    About
                  </button>
                  <button
                    className={`py-2 px-4 ${
                      activeTab === "notes"
                        ? "text-gray-700 border-b-2 border-green-600"
                        : "text-gray-500"
                    }`}
                    onClick={() => setActiveTab("notes")}
                  >
                    Notes
                  </button>
                </div>
              </div>
            </div>

            <div className="px-8 py-2">
              <div className="max-w-2xl">
                {activeTab === "about" ? (
                  <div className="space-y-8">
                    <div>
                      <div className="mt-4 space-y-4">
                        {selectedContact?.phones?.map((phone, index) => {
                          const phoneNumber =
                            typeof phone === "string"
                              ? phone
                              : (phone as any)?.phone;
                          return (
                            <div key={index} className="flex items-center">
                              <FiPhone className="text-gray-400 w-5 h-5 mr-3" />
                              <p className="text-gray-900">{phoneNumber}</p>
                            </div>
                          );
                        })}
                        {selectedContact?.email && (
                          <div className="flex items-center">
                            <FiMail className="text-gray-400 w-5 h-5 mr-3" />
                            <p className="text-gray-900">
                              {selectedContact.email}
                            </p>
                          </div>
                        )}
                        {selectedContact?.emails?.map((email, index) => {
                          const emailAddress =
                            typeof email === "string"
                              ? email
                              : (email as any)?.email;
                          return (
                            <div key={index} className="flex items-center">
                              <FiMail className="text-gray-400 w-5 h-5 mr-3" />
                              <p className="text-gray-900">{emailAddress}</p>
                            </div>
                          );
                        })}
                        <div className="flex items-center">
                          <BsBuilding className="text-gray-400 w-5 h-5 mr-3" />
                          <p className="text-gray-900">
                            {selectedContact?.company ||
                              selectedContact?.company_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {selectedContact?.address && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Address
                        </p>
                        <div className="mt-4 space-y-2">
                          <div className="flex items-start">
                            <FiMapPin className="text-gray-400 w-5 h-5 mr-3 mt-1" />
                            <div>
                              <p className="text-gray-900">
                                {selectedContact?.address?.line_1 ?? ""}
                              </p>
                              {selectedContact?.address?.line_2 && (
                                <p className="text-gray-900">
                                  {selectedContact?.address?.line_2}
                                </p>
                              )}
                              <p className="text-gray-900">
                                {selectedContact?.address?.city ?? ""}
                                {selectedContact?.address?.city ? ", " : ""}
                                {selectedContact?.address?.state ?? ""}
                                {selectedContact?.address?.zip_code ?? ""}
                              </p>
                              <p className="text-gray-900">
                                {selectedContact?.address?.country ?? ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasSocialLinks(selectedContact) && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Social Links
                        </p>
                        <div className="mt-4">
                          <SocialLinks selectedContact={selectedContact} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedContact?.note?.content && !isEditingNote ? (
                      <>
                        <div className="flex items-start justify-between">
                          <h1 className="text-gray-900">
                            {selectedContact.note.content}
                          </h1>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setIsEditingNote(true)}
                              title="Edit Note"
                            >
                              <FiEdit2 className="text-gray-500 hover:text-blue-500" />
                            </button>
                            <button
                              onClick={handleDeleteNote}
                              title="Delete Note"
                            >
                              <FiTrash2 className="text-gray-500 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                        {selectedContact.note.updated_at && (
                          <p className="text-sm text-gray-500">
                            Last updated:{" "}
                            {new Date(
                              selectedContact.note.updated_at
                            ).toLocaleString()}
                          </p>
                        )}
                      </>
                    ) : isEditingNote ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full border rounded p-2"
                          rows={4}
                          value={noteInput}
                          onChange={(e) => setNoteInput(e.target.value)}
                        />
                        <div className="flex space-x-2">
                          <button
                            className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
                            onClick={handleSaveNote}
                          >
                            Save
                          </button>
                          <button
                            className="border px-4 py-1 rounded hover:bg-gray-100"
                            onClick={() => {
                              setIsEditingNote(false);
                              setNoteInput(
                                selectedContact?.note?.content || ""
                              );
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">
                          No notes available for this contact.
                        </p>
                        <button
                          className="mt-2 text-blue-600 hover:underline"
                          onClick={() => setIsEditingNote(true)}
                        >
                          Add Note
                        </button>
                      </div>
                    )}
                    {/* Delete Note Confirmation Modal */}
                    {showDeleteNoteModal && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                          <div className="flex items-center mb-4">
                            <span className="text-red-500 mr-2">
                              <svg
                                width="20"
                                height="20"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                                />
                              </svg>
                            </span>
                            <span className="text-gray-800 font-medium">
                              Are you sure you want to delete your note?
                            </span>
                          </div>
                          <div className="flex justify-end space-x-2 mt-6">
                            <button
                              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                              onClick={() => setShowDeleteNoteModal(false)}
                            >
                              No
                            </button>
                            <button
                              className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                              onClick={confirmDeleteNote}
                            >
                              Yes
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-500 text-lg">There is no contact</p>
            </div>
          </div>
        )}
      </div>

      {/* Activities sidebar */}
      <div className="w-64 bg-white border-l border-gray-200 flex flex-col justify-between pb-8">
        <div>
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Activities
            </h2>
            {selectedContact && (
              <p className="text-sm text-gray-500 mt-1">
                {recentChats.length} activities
              </p>
            )}
          </div>
          <div className="overflow-y-auto">
            {selectedContact ? (
              recentChats.length > 0 ? (
                recentChats.map((activity) => (
                  <div
                    key={activity.id}
                    className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                      !activity.isRead
                        ? "bg-blue-50 border-l-4 border-blue-500"
                        : ""
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center">
                        <p
                          className={`font-medium ${
                            !activity.isRead ? "text-blue-900" : "text-gray-900"
                          }`}
                        >
                          {activity.name}
                        </p>
                        {!activity.isRead && (
                          <span className="ml-2 inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <p className="text-xs text-gray-500">{activity.time}</p>
                        {activity.hasEmail && (
                          <HiMail className="text-gray-400 w-4 h-4" />
                        )}
                      </div>
                    </div>
                    <p
                      className={`text-sm ${
                        !activity.isRead ? "text-blue-700" : "text-gray-500"
                      }`}
                    >
                      {activity.message}
                    </p>
                  </div>
                ))
              ) : (
                <div className="px-4 py-8 text-center">
                  <p className="text-gray-500 text-sm">No activities found</p>
                </div>
              )
            ) : (
              <div className="px-4 py-8 text-center">
                <p className="text-gray-500 text-sm">
                  Select a contact to view activities
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 ">
          <div className="flex flex-col space-y-2">
            <p className="text-sm font-medium text-gray-500">More</p>
            <button className="text-left text-gray-500 hover:bg-gray-50 px-2 py-1 rounded">
              <p className="text-sm">Archive</p>
            </button>
            <button className="text-left text-gray-500 hover:bg-gray-50 px-2 py-1 rounded">
              <p className="text-sm">Delete</p>
            </button>
          </div>
        </div>
      </div>

      <ContactModal
        key={`${modalMode}-${isModalOpen}-${selectedContact?.id || "none"}`}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        contactToEdit={selectedContact || undefined}
        setContacts={setContacts}
        setSelectedContact={setSelectedContact}
        onRefetch={fetchContacts}
      />
      {showDeleteContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <div className="flex items-center mb-4">
              <span className="text-red-500 mr-2">
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                  />
                </svg>
              </span>
              <span className="text-gray-800 font-medium">
                Are you sure you want to delete{" "}
                {(selectedContact as any).firstName ??
                  selectedContact?.first_name}{" "}
                {(selectedContact as any).lastName ??
                  selectedContact?.last_name}
                ?
              </span>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowDeleteContactModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                onClick={handleDeleteContact}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
